import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useState, useEffect } from "react";
import { Send, Users, Mail, Clock, CheckCircle, XCircle } from "lucide-react";

const NewsletterAdmin = () => {
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [stats, setStats] = useState({ active_subscribers: 0, recent_newsletters: [] });
  const { toast } = useToast();

  // Load stats on component mount
  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('newsletter_send_fixed_20251219_1800', {
        method: 'GET'
      });
      
      if (error) throw error;
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const sendTestNewsletter = async () => {
    if (!subject || !content || !testEmail) {
      toast({
        title: "Champs requis",
        description: "Veuillez remplir tous les champs pour l'email de test",
        variant: "destructive",
      });
      return;
    }

    setIsSending(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('newsletter_send_fixed_20251219_1800', {
        body: {
          subject,
          content,
          testEmail
        }
      });
      
      if (error) throw error;
      
      toast({
        title: "Email de test envoyé !",
        description: `Test envoyé à ${testEmail}`,
      });
      
    } catch (error: any) {
      toast({
        title: "Erreur d'envoi",
        description: error.message || "Une erreur est survenue",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  const sendNewsletter = async () => {
    if (!subject || !content) {
      toast({
        title: "Champs requis",
        description: "Veuillez remplir le sujet et le contenu",
        variant: "destructive",
      });
      return;
    }

    const confirmed = window.confirm(
      `Êtes-vous sûr de vouloir envoyer cette newsletter à ${stats.active_subscribers} abonnés ?`
    );
    
    if (!confirmed) return;

    setIsSending(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('newsletter_send_fixed_20251219_1800', {
        body: {
          subject,
          content
        }
      });
      
      if (error) throw error;
      
      toast({
        title: "Newsletter envoyée !",
        description: `Envoyée à ${data.successful_sends} abonnés sur ${data.total_recipients}`,
      });
      
      // Clear form and reload stats
      setSubject('');
      setContent('');
      loadStats();
      
    } catch (error: any) {
      toast({
        title: "Erreur d'envoi",
        description: error.message || "Une erreur est survenue",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-8">
          
          {/* Header */}
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-2">Administration Newsletter</h1>
            <p className="text-muted-foreground">
              Envoyez des newsletters à vos abonnés
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Abonnés actifs</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.active_subscribers}</div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Newsletters envoyées</CardTitle>
                <Mail className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.recent_newsletters?.length || 0}</div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Dernière newsletter</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-sm">
                  {stats.recent_newsletters?.[0] 
                    ? new Date(stats.recent_newsletters[0].sent_at).toLocaleDateString('fr-FR')
                    : 'Aucune'
                  }
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Newsletter Form */}
          <Card>
            <CardHeader>
              <CardTitle>Créer une newsletter</CardTitle>
              <CardDescription>
                Rédigez et envoyez une newsletter à tous vos abonnés
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="subject">Sujet</Label>
                <Input
                  id="subject"
                  placeholder="Ex: Nouvelles offres BT-Connect - Décembre 2024"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="content">Contenu</Label>
                <Textarea
                  id="content"
                  placeholder="Rédigez votre newsletter ici..."
                  className="min-h-[200px]"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Le contenu sera automatiquement formaté en HTML pour l'email
                </p>
              </div>

              {/* Test Section */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4">Test avant envoi</h3>
                <div className="flex gap-3">
                  <Input
                    placeholder="Email de test"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    className="flex-1"
                  />
                  <Button 
                    variant="outline" 
                    onClick={sendTestNewsletter}
                    disabled={isSending || !subject || !content || !testEmail}
                  >
                    <Send className="mr-2 h-4 w-4" />
                    Test
                  </Button>
                </div>
              </div>

              {/* Send Button */}
              <div className="border-t pt-6">
                <Button 
                  onClick={sendNewsletter}
                  disabled={isSending || !subject || !content || stats.active_subscribers === 0}
                  className="w-full"
                  size="lg"
                >
                  <Send className="mr-2 h-4 w-4" />
                  {isSending 
                    ? 'Envoi en cours...' 
                    : `Envoyer à ${stats.active_subscribers} abonnés`
                  }
                </Button>
                {stats.active_subscribers === 0 && (
                  <p className="text-sm text-muted-foreground mt-2 text-center">
                    Aucun abonné actif trouvé
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recent Newsletters */}
          {stats.recent_newsletters && stats.recent_newsletters.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Newsletters récentes</CardTitle>
                <CardDescription>
                  Historique des dernières newsletters envoyées
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {stats.recent_newsletters.map((newsletter: any) => (
                    <div key={newsletter.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex-1">
                        <h4 className="font-medium">{newsletter.subject}</h4>
                        <p className="text-sm text-muted-foreground">
                          {new Date(newsletter.sent_at).toLocaleString('fr-FR')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {newsletter.is_test && (
                          <Badge variant="secondary">Test</Badge>
                        )}
                        <div className="flex items-center gap-1 text-sm">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          {newsletter.successful_sends}
                        </div>
                        {newsletter.failed_sends > 0 && (
                          <div className="flex items-center gap-1 text-sm">
                            <XCircle className="h-4 w-4 text-red-500" />
                            {newsletter.failed_sends}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Instructions */}
          <Card>
            <CardHeader>
              <CardTitle>Instructions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">1</div>
                <div>
                  <p className="font-medium">Rédigez votre newsletter</p>
                  <p className="text-sm text-muted-foreground">Saisissez un sujet accrocheur et un contenu informatif</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">2</div>
                <div>
                  <p className="font-medium">Testez avant d'envoyer</p>
                  <p className="text-sm text-muted-foreground">Envoyez un email de test à votre adresse pour vérifier le rendu</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">3</div>
                <div>
                  <p className="font-medium">Envoyez à tous vos abonnés</p>
                  <p className="text-sm text-muted-foreground">Une fois satisfait, envoyez la newsletter à tous vos abonnés actifs</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default NewsletterAdmin;